import { IMAGE_ACCEPT, useFilesOnDrop } from "@/lib/messages";
import { classNames } from "@/lib/utils";
import { Modal } from "@components/library/AppDialog";
import LoadingIndicator from "@components/library/LoadingIndicator";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

interface Props {
  open: boolean;
  onClose: () => void;
  onDrop: (dataUrls: string[]) => void;
}

export const UploadImageDialog = ({ open, onClose, onDrop }: Props) => {
  const [dropped, setDropped] = useState(false);

  // Convert files to data URLs.
  const _filesOnDrop = useFilesOnDrop(onDrop);

  // Sets `dropped` to show loading states
  const filesOnDrop = useCallback(
    async (files: File[]) => {
      setDropped(true);
      await _filesOnDrop(files);
      setDropped(false);
    },
    [_filesOnDrop],
  );

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    multiple: true,
    accept: IMAGE_ACCEPT,
    onDrop: filesOnDrop,
  });
  return (
    <Modal open={open} onClose={onClose}>
      <div
        className={classNames(
          "relative cursor-pointer rounded border-2 border-dashed bg-gray-100 px-12 py-8 hover:bg-gray-50",
          isFocused
            ? "border-blue-150"
            : isDragAccept
              ? "border-green-500"
              : isDragReject
                ? "border-red-700"
                : "border-gray-200",
        )}
      >
        <div
          {...getRootProps({
            className: "text-11-14 py-24 flex flex-col gap-12 items-center justify-center text-gray-800",
          })}
        >
          <input {...getInputProps()} />
          <p className="text-16-24 text-gray-600">Select or drag and drop an image to upload</p>
          <p className="text-11-14 text-gray-400">Images above 300kB or 2048×768px will be resized and compressed.</p>
        </div>
        {dropped && (
          <div className="absolute inset-0 flex items-center justify-center bg-white">
            <LoadingIndicator className="h-36 w-36" />
          </div>
        )}
      </div>
    </Modal>
  );
};
